import { useCallback, useEffect, useState } from 'react';
import AuthService from '../../services/AuthService';
import WorkspaceSettingsService from '../../services/WorkspaceSettingsService';
import ClientSchedulerSettingsPanel from './ClientSchedulerSettingsPanel';

const NOTIFICATION_STORAGE_KEY = 'nf_workspace_notification_prefs_v1';

const card = {
  background: '#fff',
  borderRadius: 16,
  boxShadow: '0 4px 24px rgba(0,0,0,.08)',
  padding: 28,
};

const inp = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid #e9ecef',
  fontSize: 14,
  color: '#344767',
  background: '#fff',
};

function loadNotificationPrefs() {
  try {
    const raw = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
    if (!raw) return { dueFollowUpAlerts: true, overdueBadge: true, soundHints: false };
    const o = JSON.parse(raw);
    return {
      dueFollowUpAlerts: o.dueFollowUpAlerts !== false,
      overdueBadge: o.overdueBadge !== false,
      soundHints: !!o.soundHints,
    };
  } catch {
    return { dueFollowUpAlerts: true, overdueBadge: true, soundHints: false };
  }
}

export default function WorkspaceSettings() {
  const clientId = AuthService.getLoggedInClientId();
  const roleNum = parseInt(AuthService.getLoggedInRoleId(), 10);
  const canEdit = [2, 3].includes(roleNum);

  const [tab, setTab] = useState('notifications');
  const [toast, setToast] = useState(null);
  const [notif, setNotif] = useState(loadNotificationPrefs);

  const [fence, setFence] = useState({
    officeLatitude: '',
    officeLongitude: '',
    radiusMeters: '',
  });
  const [fenceLoading, setFenceLoading] = useState(false);

  const showMsg = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const persistNotif = (next) => {
    setNotif(next);
    localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(next));
    showMsg('Notification preferences saved on this device.');
  };

  const loadFence = useCallback(async () => {
    if (!clientId) return;
    setFenceLoading(true);
    try {
      const data = await WorkspaceSettingsService.getAttendanceGeoFence(clientId);
      setFence({
        officeLatitude: data.officeLatitude != null ? String(data.officeLatitude) : '',
        officeLongitude: data.officeLongitude != null ? String(data.officeLongitude) : '',
        radiusMeters: data.radiusMeters != null && data.radiusMeters > 0 ? String(data.radiusMeters) : '',
      });
    } catch {
      showMsg('Could not load attendance area settings.', 'error');
    } finally {
      setFenceLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (tab === 'attendance' && clientId) loadFence();
  }, [tab, clientId, loadFence]);

  const saveFence = async () => {
    if (!clientId || !canEdit) return;
    const lat = fence.officeLatitude.trim() === '' ? null : Number(fence.officeLatitude);
    const lng = fence.officeLongitude.trim() === '' ? null : Number(fence.officeLongitude);
    const r = fence.radiusMeters.trim() === '' ? null : parseInt(fence.radiusMeters, 10);
    if (lat != null && (Number.isNaN(lat) || lat < -90 || lat > 90)) {
      showMsg('Latitude must be between -90 and 90.', 'error');
      return;
    }
    if (lng != null && (Number.isNaN(lng) || lng < -180 || lng > 180)) {
      showMsg('Longitude must be between -180 and 180.', 'error');
      return;
    }
    if (r != null && (Number.isNaN(r) || r <= 0)) {
      showMsg('Radius must be a positive number of meters, or leave empty to disable.', 'error');
      return;
    }
    try {
      await WorkspaceSettingsService.updateAttendanceGeoFence(clientId, {
        officeLatitude: lat,
        officeLongitude: lng,
        radiusMeters: r,
      });
      showMsg('Attendance area saved. Employees must check in within this radius.');
      loadFence();
    } catch {
      showMsg('Save failed. You may need Admin-level permissions.', 'error');
    }
  };

  const disableFence = async () => {
    setFence({ officeLatitude: '', officeLongitude: '', radiusMeters: '' });
    if (!clientId || !canEdit) return;
    try {
      await WorkspaceSettingsService.updateAttendanceGeoFence(clientId, {
        officeLatitude: null,
        officeLongitude: null,
        radiusMeters: null,
      });
      showMsg('Attendance geo rule disabled.');
      loadFence();
    } catch {
      showMsg('Could not disable rule.', 'error');
    }
  };

  if (!clientId) {
    return (
      <div style={{ padding: 48, color: '#8392ab' }}>
        Workspace settings require a tenant account with a client assignment.
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: '#f4f7fa', minHeight: '100vh' }}>
      <style>{`
        .ws-tab:hover { background: #eef1f6 !important; color: #344767 !important; }
        .ws-tab.active { background: linear-gradient(135deg,#5e72e4,#825ee4) !important; color: #fff !important; box-shadow: 0 4px 12px rgba(94,114,228,0.35); }
      `}</style>

      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 9999,
            background: toast.type === 'error' ? '#f5365c' : '#2dce89',
            color: '#fff',
            padding: '14px 28px',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,.2)',
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          {toast.msg}
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#1a1f36' }}>
          <i className="fas fa-sliders-h" style={{ color: '#5e72e4', marginRight: 10 }} />
          Workspace settings
        </h3>
        <p style={{ margin: '8px 0 0', color: '#8898aa', fontSize: 14 }}>
          Manage your agency workspace — display preferences, attendance area, and lead automation.
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 6,
          marginBottom: 20,
          background: '#fff',
          borderRadius: 12,
          padding: 6,
          width: 'fit-content',
          boxShadow: '0 2px 8px rgba(0,0,0,.06)',
          flexWrap: 'wrap',
        }}
      >
        {[
          ['notifications', 'fas fa-desktop', 'Display'],
          ['attendance', 'fas fa-map-marker-alt', 'Attendance area'],
          ['scheduler', 'fas fa-clock', 'Scheduler settings'],
        ].map(([key, icon, label]) => (
          <button
            key={key}
            type="button"
            className={`ws-tab${tab === key ? ' active' : ''}`}
            onClick={() => setTab(key)}
            style={{
              padding: '9px 20px',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 14,
              background: tab === key ? undefined : 'transparent',
              color: tab === key ? undefined : '#8898aa',
            }}
          >
            <i className={icon} style={{ marginRight: 7 }} />
            {label}
          </button>
        ))}
      </div>

      {tab === 'notifications' && (
        <div style={card}>
          <h4 style={{ marginTop: 0, color: '#344767' }}>Display preferences</h4>
          <p style={{ color: '#8898aa', fontSize: 13, marginBottom: 20 }}>
            These preferences apply only on this device (local storage).
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <input
              type="checkbox"
              checked={notif.dueFollowUpAlerts}
              onChange={(e) => persistNotif({ ...notif, dueFollowUpAlerts: e.target.checked })}
            />
            <span style={{ fontWeight: 600, color: '#344767' }}>Highlight due follow-ups in navigation</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <input
              type="checkbox"
              checked={notif.overdueBadge}
              onChange={(e) => persistNotif({ ...notif, overdueBadge: e.target.checked })}
            />
            <span style={{ fontWeight: 600, color: '#344767' }}>Show overdue counts on the menu badge</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="checkbox"
              checked={notif.soundHints}
              onChange={(e) => persistNotif({ ...notif, soundHints: e.target.checked })}
            />
            <span style={{ fontWeight: 600, color: '#344767' }}>Allow subtle UI sounds for confirmations</span>
          </label>
        </div>
      )}

      {tab === 'attendance' && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid #f0f2f5' }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: 'linear-gradient(135deg,#2dce89,#11cdef)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 22, flexShrink: 0 }}>
              <i className="fas fa-map-marker-alt" />
            </div>
            <div>
              <h4 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#344767' }}>Office geo-fence</h4>
              <p style={{ margin: '6px 0 0', color: '#8898aa', fontSize: 13, lineHeight: 1.6, maxWidth: 640 }}>
                When latitude, longitude and a radius (meters) are set, employees must share GPS on check-in/check-out
                and must be within that distance of the office point.
              </p>
            </div>
          </div>

          {!canEdit && (
            <div style={{ padding: 12, background: '#fff8e6', borderRadius: 10, marginBottom: 20, fontSize: 13, color: '#856404' }}>
              Only Admin or Manager can change these values.
            </div>
          )}

          {fenceLoading ? (
            <p style={{ color: '#8392ab', textAlign: 'center', padding: 32 }}>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />
              Loading…
            </p>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16, marginBottom: 20 }}>
                <label style={{ display: 'block', minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#8392ab', marginBottom: 8 }}>Office latitude (WGS84)</span>
                  <input style={inp} disabled={!canEdit} placeholder="e.g. 28.6139" value={fence.officeLatitude} onChange={(e) => setFence((f) => ({ ...f, officeLatitude: e.target.value }))} />
                </label>
                <label style={{ display: 'block', minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#8392ab', marginBottom: 8 }}>Office longitude</span>
                  <input style={inp} disabled={!canEdit} placeholder="e.g. 77.2090" value={fence.officeLongitude} onChange={(e) => setFence((f) => ({ ...f, officeLongitude: e.target.value }))} />
                </label>
                <label style={{ display: 'block', minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#8392ab', marginBottom: 8 }}>Radius (meters)</span>
                  <input style={inp} disabled={!canEdit} placeholder="e.g. 200 — empty to disable" value={fence.radiusMeters} onChange={(e) => setFence((f) => ({ ...f, radiusMeters: e.target.value }))} />
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap', paddingTop: 8, borderTop: '1px solid #f0f2f5' }}>
                <button
                  type="button"
                  onClick={() => {
                    if (!navigator.geolocation) { showMsg('Geolocation not supported.', 'error'); return; }
                    navigator.geolocation.getCurrentPosition(
                      (p) => {
                        setFence((f) => ({
                          ...f,
                          officeLatitude: String(p.coords.latitude.toFixed(7)),
                          officeLongitude: String(p.coords.longitude.toFixed(7)),
                        }));
                        showMsg('Filled coordinates from this device.');
                      },
                      () => showMsg('Could not read GPS.', 'error'),
                    );
                  }}
                  disabled={!canEdit}
                  style={{ padding: '11px 20px', borderRadius: 10, border: '1px solid #5e72e4', background: '#fff', color: '#5e72e4', fontWeight: 700, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.5 }}
                >
                  <i className="fas fa-crosshairs" style={{ marginRight: 8 }} />
                  Use my location
                </button>
                <button type="button" disabled={!canEdit} onClick={disableFence} style={{ padding: '11px 20px', borderRadius: 10, border: '1px solid #e9ecef', background: '#fff', color: '#344767', fontWeight: 700, cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.5 }}>
                  Disable geo rule
                </button>
                <button type="button" disabled={!canEdit} onClick={saveFence} style={{ padding: '11px 24px', borderRadius: 10, border: 'none', background: canEdit ? 'linear-gradient(135deg,#2dce89,#11cdef)' : '#adb5bd', color: '#fff', fontWeight: 700, cursor: canEdit ? 'pointer' : 'not-allowed' }}>
                  <i className="fas fa-save" style={{ marginRight: 8 }} />
                  Save attendance rule
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'scheduler' && (
        <div style={card}>
          <ClientSchedulerSettingsPanel clientId={clientId} canEdit={canEdit} onMessage={showMsg} />
        </div>
      )}
    </div>
  );
}
